import { CheckCircle, Info, Spinner } from "@phosphor-icons/react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import * as yup from "yup";
import CountryInput from "./CountryInput";
import { useAuth } from "../context/AuthContext";
import useResolveGeo from "../hooks/useResolveGeo";
import {
  useAddressFieldRules,
  useComplianceAddress,
  usePostcodeLookup,
  useResolveAddressSuggestion,
  useSaveComplianceAddress,
  ComplianceAddress,
} from "../hooks/useComplianceAddress";

const schema = yup.object().shape({
  line_1: yup.string().required("Street address is required"),
  city: yup.string().required("City or town is required"),
});

const fieldClass =
  "rounded-lg border border-gray-300 shadow px-4 py-3 w-full focus:outline-none focus-within:outline-deepBlue";
const labelClass = "text-sm font-bold text-gray-700";

/** Small debounce so the postcode lookup is not one request per keystroke. */
function useDebounced<T>(value: T, delay = 500): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Home address capture for customers anywhere.
 *
 * Shared between the signup funnel and the compliance settings screen, which
 * differ only in chrome. The form itself asks the server what a given country
 * needs rather than hardcoding rules, because the shape genuinely differs: Hong
 * Kong has no postcodes at all, Nigeria has NIPOST codes almost nobody uses,
 * the UK's are mandatory. A fixed form would either block a legitimate customer
 * on a field their country does not have, or let one through without a field it
 * does.
 *
 * Loqate suggestions appear when that paid provider is configured. The free
 * provider and every lookup failure keep the manual fields fully usable.
 */
const AddressForm: FC<{
  onSaved: () => void;
  submitLabel?: string;
  initialAddress?: ComplianceAddress | null;
  onSubmitAddress?: (address: ComplianceAddress) => void;
  isSaving?: boolean;
}> = ({
  onSaved,
  submitLabel = "Save address",
  initialAddress,
  onSubmitAddress,
  isSaving = false,
}) => {
  const { user, refetch } = useAuth();
  const { detectedCountry } = useResolveGeo();
  const saved = useComplianceAddress(initialAddress === undefined);
  const address = initialAddress === undefined ? saved.address : initialAddress;
  const isAddressVerified = initialAddress === undefined && saved.isAddressVerified;
  const isLoadingAddress = initialAddress === undefined && saved.isLoadingAddress;

  const initialCountry =
    address?.country ?? detectedCountry ?? user?.residence_country ?? "NG";
  const [country, setCountry] = useState(initialCountry);
  const [postcode, setPostcode] = useState(address?.postcode ?? "");
  const edited = useRef(false);
  const selectionVersion = useRef(0);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const postcodeInput = useRef<HTMLInputElement>(null);
  const suggestionList = useRef<HTMLDivElement>(null);

  // Both the saved address and the detected country arrive after first render,
  // so adopt them when they land instead of leaving the form on its fallback.
  useEffect(() => {
    if (edited.current) return;
    if (address?.country) setCountry(address.country);
    if (address?.postcode) setPostcode(address.postcode);
  }, [address?.country, address?.postcode]);

  useEffect(() => {
    if (edited.current) return;
    if (!address?.country && detectedCountry) setCountry(detectedCountry);
  }, [address?.country, detectedCountry]);

  const { rules } = useAddressFieldRules(country);
  const debouncedPostcode = useDebounced(postcode);
  const { lookup, isLookingUpPostcode, postcodeError } = usePostcodeLookup(
    rules?.uses_postcodes ? country : null,
    debouncedPostcode
  );
  const { resolveSuggestion, resolvingSuggestion } = useResolveAddressSuggestion();
  const suggestions = postcode.trim() === debouncedPostcode.trim()
    ? (lookup?.addresses ?? []).filter(item => item.type === "Address") : [];
  const showSuggestions = suggestionsOpen && suggestions.length > 0;
  useEffect(() => { setActiveSuggestion(-1); }, [postcode, country]);
  useEffect(() => {
    suggestionList.current?.children[activeSuggestion]?.scrollIntoView({ block: "nearest" });
  }, [activeSuggestion]);

  const { saveAddress, savingAddress } = useSaveComplianceAddress({
    onSuccess() {
      toast.success("Address saved");
      refetch();
      onSaved();
    },
    onError(error) {
      toast.error(error?.message || "Could not save your address");
    },
  });

  const postcodeHint = useMemo(() => {
    if (!rules?.uses_postcodes) return null;
    if (isLookingUpPostcode) return { tone: "muted" as const, text: "Checking postcode..." };
    if (postcodeError) return { tone: "error" as const, text: postcodeError };
    if (lookup?.city) {
      return {
        tone: "ok" as const,
        text: [lookup.city, lookup.state].filter(Boolean).join(", "),
      };
    }
    if (rules.postcode_example) {
      return { tone: "muted" as const, text: `For example: ${rules.postcode_example}` };
    }
    return null;
  }, [rules, isLookingUpPostcode, postcodeError, lookup]);

  return (
    <div className="flex flex-col gap-5 w-full">
      {isAddressVerified && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex gap-3 items-start">
          <CheckCircle weight="fill" className="size-5 shrink-0 mt-0.5" />
          <span className="text-sm">
            This address has been verified. Saving a new one will replace it.
          </span>
        </div>
      )}

      {!isLoadingAddress && <Formik
        initialValues={{
          line_1: address?.line_1 ?? "",
          line_2: address?.line_2 ?? "",
          city: address?.city ?? "",
          state: address?.state ?? "",
        }}
        validationSchema={schema}
        onSubmit={(values) => {
          (onSubmitAddress ?? saveAddress)({
            line_1: values.line_1,
            line_2: values.line_2,
            city: values.city,
            state: values.state,
            country,
            // Dropped rather than sent empty when the country has none, so the
            // server never stores a postcode for a place that has no postcodes.
            postcode: rules?.uses_postcodes === false ? null : postcode,
          });
        }}
      >
        {({ values, setFieldValue }) => (
          <Form className="flex flex-col gap-5" onChange={() => {
            edited.current = true;
            selectionVersion.current += 1;
          }}>
            <div className="flex flex-col gap-2">
              <label className={labelClass}>Country*</label>
              <CountryInput
                selected={country}
                onSelect={(code) => {
                  edited.current = true;
                  selectionVersion.current += 1;
                  setCountry(code);
                  // A postcode is meaningless once the country changes, and
                  // keeping it would validate against the wrong rules.
                  setPostcode("");
                  setSuggestionsOpen(false);
                }}
              />
            </div>

            {rules?.uses_postcodes !== false && (
              <div className="flex flex-col gap-2 max-w-md">
                <label htmlFor="postcode" className={labelClass}>
                  Postcode{rules?.postcode_required ? "*" : ""}
                </label>
                <div className="relative" onBlur={event => {
                  if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setSuggestionsOpen(false);
                }}>
                  <input
                    id="postcode"
                    name="postcode"
                    ref={postcodeInput}
                    role="combobox"
                    aria-autocomplete="list"
                    aria-expanded={showSuggestions}
                    aria-controls="postcode-suggestions"
                    aria-activedescendant={showSuggestions && activeSuggestion >= 0 ? `postcode-option-${activeSuggestion}` : undefined}
                    value={postcode}
                    onFocus={() => setSuggestionsOpen(true)}
                    onChange={(e) => { setPostcode(e.target.value); setSuggestionsOpen(true); }}
                    onKeyDown={event => {
                      if (event.key === "Escape") { setSuggestionsOpen(false); return; }
                      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                        event.preventDefault(); setSuggestionsOpen(true);
                        setActiveSuggestion(index => !suggestions.length ? -1 : index < 0 ? (event.key === "ArrowDown" ? 0 : suggestions.length - 1) : (index + (event.key === "ArrowDown" ? 1 : -1) + suggestions.length) % suggestions.length);
                      }
                      if (event.key === "Enter" && showSuggestions && activeSuggestion >= 0) {
                        event.preventDefault(); (suggestionList.current?.children[activeSuggestion] as HTMLButtonElement)?.click();
                      }
                    }}
                    placeholder={rules?.postcode_example ?? "Enter your postcode"}
                    className={fieldClass}
                    autoComplete="postal-code"
                  />
                  {isLookingUpPostcode && (
                    <Spinner className="size-4 animate-spin absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  )}
                {showSuggestions && (
                  <div ref={suggestionList} id="postcode-suggestions" role="listbox" aria-label="Matching addresses"
                    className="absolute top-full left-0 right-0 z-30 mt-1 max-h-60 overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white shadow-lg">
                    {suggestions.map((suggestion, index) => (
                      <button key={suggestion.id} id={`postcode-option-${index}`} type="button" role="option"
                        aria-selected={activeSuggestion === index} disabled={resolvingSuggestion} tabIndex={-1}
                        onMouseDown={event => event.preventDefault()}
                        onClick={async () => {
                          try {
                            const version = ++selectionVersion.current;
                            const response = await resolveSuggestion({ id: suggestion.id, country });
                            if (version !== selectionVersion.current) return;
                            const selected = response.data?.address;
                            if (!selected) return;
                            edited.current = true;
                            setFieldValue("line_1", selected.line_1 ?? "");
                            setFieldValue("line_2", selected.line_2 ?? "");
                            setFieldValue("city", selected.city ?? "");
                            setFieldValue("state", selected.state ?? "");
                            setPostcode(selected.postcode ?? postcode);
                            postcodeInput.current?.focus();
                            setSuggestionsOpen(false);
                            toast.success("Address filled in. Check the details below, then save.");
                          } catch (error: any) { toast.error(error?.message || "Could not load that address"); }
                        }}
                        className={`block w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-orange-50 focus:bg-orange-50 disabled:opacity-50 ${activeSuggestion === index ? "bg-orange-50" : ""}`}>
                        <span className="block text-sm font-medium text-gray-800">{suggestion.label}</span>
                        {suggestion.description && <span className="block text-xs text-gray-500">{suggestion.description}</span>}
                      </button>
                    ))}
                  </div>
                )}
                </div>
                {postcodeHint && (
                  <span
                    className={
                      postcodeHint.tone === "error"
                        ? "text-sm text-red-500"
                        : postcodeHint.tone === "ok"
                        ? "text-sm text-green-600"
                        : "text-sm text-gray-500"
                    }
                  >
                    {postcodeHint.text}
                  </span>
                )}
                {lookup?.city && !values.city && (
                  <button
                    type="button"
                    onClick={() => {
                      setFieldValue("city", lookup.city ?? "");
                      setFieldValue("state", lookup.state ?? "");
                    }}
                    className="text-sm text-visaro-orange font-medium text-left"
                  >
                    Use {lookup.city}
                    {lookup.state ? `, ${lookup.state}` : ""}
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label htmlFor="line_1" className={labelClass}>
                House number and street*
              </label>
              <Field
                id="line_1"
                name="line_1"
                placeholder="e.g. 2 Wisteria Close"
                className={fieldClass}
                autoComplete="address-line1"
              />
              <ErrorMessage name="line_1" component="div" className="text-red-500 text-sm" />
            </div>

            <div className="flex flex-col gap-2">
              <label htmlFor="line_2" className={labelClass}>
                Apartment, suite, building (optional)
              </label>
              <Field
                id="line_2"
                name="line_2"
                placeholder="Flat 4B"
                className={fieldClass}
                autoComplete="address-line2"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label htmlFor="city" className={labelClass}>
                  City or town*
                </label>
                <Field
                  id="city"
                  name="city"
                  placeholder="London"
                  className={fieldClass}
                  autoComplete="address-level2"
                />
                <ErrorMessage name="city" component="div" className="text-red-500 text-sm" />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="state" className={labelClass}>
                  State, province or region (optional)
                </label>
                <Field
                  id="state"
                  name="state"
                  placeholder="Greater London"
                  className={fieldClass}
                  autoComplete="address-level1"
                />
              </div>
            </div>

            {rules && rules.uses_postcodes && !rules.supports_postcode_lookup && (
              <div className="bg-gray-50 border border-gray-200 text-gray-600 rounded-lg p-4 flex gap-3 items-start">
                <Info className="size-5 shrink-0 mt-0.5" />
                <span className="text-sm">
                  Please type your house number and street in full. We check that
                  your postcode is valid, but we cannot list the addresses on it
                  yet.
                </span>
              </div>
            )}

            <button
              type="submit"
              disabled={savingAddress || isSaving || isLoadingAddress}
              className="bg-visaro-orange text-white rounded-xl px-8 py-3 font-medium self-start disabled:opacity-60"
            >
              {savingAddress || isSaving ? "Saving..." : submitLabel}
            </button>
          </Form>
        )}
      </Formik>}
    </div>
  );
};

export default AddressForm;
